<script setup lang="ts">
/**
 * The first-login tips of a supervisor or an agent: how their space works,
 * on one screen, once. Seen again from the help.
 */
import { computed, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { api } from '@/lib/api'
import { session } from '@/lib/auth'
import Button from '@/ui/Button.vue'
import Dialog from '@/ui/Dialog.vue'
import { introKind, useIntro } from './onboarding'

const p = defineProps<{ again?: boolean }>()
const emit = defineEmits<{ closed: [] }>()
const { t } = useI18n()
const qc = useQueryClient()
const intro = useIntro()
const open = ref(false)
watch(() => [intro.data.value?.seen, p.again], ([seen, again]) => { if (seen === false || again) open.value = true }, { immediate: true })
const kind = computed(introKind)
const tips = computed(() => [1, 2, 3, 4].map((n) => t(`onboarding.intro.${kind.value}.tip${n}`)))

async function close() {
  open.value = false
  emit('closed')
  if (intro.data.value?.seen === false) {
    // Not recorded (network, server): the tips simply come back next time.
    try { await api('/onboarding/intro', { method: 'POST' }) } catch { return }
    qc.setQueryData(['onboarding-intro', session.user?.id], { seen: true })
  }
}
</script>

<template>
  <Dialog :open="open" size="md" :title="t('onboarding.intro.title', { name: session.user?.fullName ?? '' })"
          :description="t(`onboarding.intro.${kind}.subtitle`)" @close="close">
    <ol class="m-0 grid list-decimal gap-2.5 ps-5 text-[13.5px]">
      <li class="motion-row" v-for="(tip, i) in tips" :style="{ '--i': i }" :key="i">{{ tip }}</li>
    </ol>
    <template #footer>
      <span class="flex-1" />
      <Button @click="close">{{ t('onboarding.intro.ok') }}</Button>
    </template>
  </Dialog>
</template>
